import { Component, type ErrorInfo, type ReactNode } from "react";

type FallbackProps = {
  error: Error;
  resetErrorBoundary: () => void;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  FallbackComponent: (props: FallbackProps) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  didCatch: boolean;
  error: Error | null;
};

const INITIAL_STATE: ErrorBoundaryState = {
  didCatch: false,
  error: null,
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = INITIAL_STATE;

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      didCatch: true,
      error,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  resetErrorBoundary = () => {
    if (!this.state.didCatch) return;

    this.props.onReset?.();
    this.setState(INITIAL_STATE);
  };

  render() {
    const { children, FallbackComponent } = this.props;
    const { didCatch, error } = this.state;

    if (didCatch && error) {
      return (
        <FallbackComponent
          error={error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }

    return children;
  }
}

export type { FallbackProps };
